/* ------------------------------------------------------------------------------
 *
 *  # Chart Module
 *
 *  Chart rendering functionality (D3 compatible)
 *
 * ---------------------------------------------------------------------------- */

// Small chart renderer for enrollmentChart (D3 v3-compatible if available, otherwise SVG fallback)
export function initEnrollmentChart() {
    (function () {
        function calcMA(series, windowSize) {
            var ma = [];
            for (var i = 0; i < series.length; i++) {
                var start = Math.max(0, i - windowSize + 1);
                var sum = 0, cnt = 0;
                for (var k = start; k <= i; k++) { sum += series[k]; cnt++; }
                ma[i] = sum / cnt;
            }
            return ma;
        }

        function createTooltip() {
            var tooltip = document.createElement('div');
            tooltip.style.position = 'absolute';
            tooltip.style.pointerEvents = 'none';
            tooltip.style.background = '#fff';
            tooltip.style.border = '1px solid rgba(0,0,0,0.08)';
            tooltip.style.padding = '6px 8px';
            tooltip.style.borderRadius = '4px';
            tooltip.style.boxShadow = '0 2px 6px rgba(0,0,0,0.08)';
            tooltip.style.fontSize = '12px';
            tooltip.style.color = '#222';
            tooltip.style.display = 'none';
            document.body.appendChild(tooltip);
            return tooltip;
        }

        function renderSvg(el, series, startYear) {
            if (!el) return;
            while (el.firstChild) el.removeChild(el.firstChild);
            var padding = { top: 20, right: 10, bottom: 40, left: 40 };
            var width = el.clientWidth || 400;
            var height = parseInt(el.style.height, 10) || 300;
            var innerWidth = Math.max(0, width - padding.left - padding.right);
            var innerHeight = Math.max(0, height - padding.top - padding.bottom);
            var max = Math.max.apply(null, series.concat([1]));
            var band = innerWidth / series.length;
            var barWidth = Math.max(4, Math.floor(band * 0.88));
            var xmlns = 'http://www.w3.org/2000/svg';
            var svg = document.createElementNS(xmlns, 'svg');
            svg.setAttribute('width', width);
            svg.setAttribute('height', height);
            svg.setAttribute('viewBox', '0 0 ' + width + ' ' + height);
            svg.style.display = 'block';
            var g = document.createElementNS(xmlns, 'g');
            g.setAttribute('transform', 'translate(' + padding.left + ',' + padding.top + ')');
            svg.appendChild(g);

            // y grid + labels
            var ticks = 4;
            for (var i = 0; i <= ticks; i++) {
                var y = innerHeight - Math.round((i / ticks) * innerHeight);
                var value = Math.round((i / ticks) * max);
                var line = document.createElementNS(xmlns, 'line');
                line.setAttribute('x1', 0);
                line.setAttribute('x2', innerWidth);
                line.setAttribute('y1', y);
                line.setAttribute('y2', y);
                line.setAttribute('stroke', '#e9ecef');
                line.setAttribute('stroke-width', '1');
                g.appendChild(line);
                var text = document.createElementNS(xmlns, 'text');
                text.setAttribute('x', -8);
                text.setAttribute('y', y + 4);
                text.setAttribute('fill', '#6c757d');
                text.setAttribute('font-size', '10');
                text.setAttribute('text-anchor', 'end');
                text.textContent = value;
                g.appendChild(text);
            }

            var palette = ['#4dabf7', '#a78bfa', '#f97316', '#34d399', '#f472b6', '#60a5fa', '#fb7185'];

            // bars
            series.forEach(function (d, i) {
                var x = Math.round(i * band + (band - barWidth) / 2);
                var h = Math.round((d / max) * innerHeight);
                var y = innerHeight - h;
                var rect = document.createElementNS(xmlns, 'rect');
                rect.setAttribute('x', x);
                rect.setAttribute('y', y);
                rect.setAttribute('width', barWidth);
                rect.setAttribute('height', h);
                rect.setAttribute('fill', palette[i % palette.length]);
                rect.setAttribute('rx', 3);
                g.appendChild(rect);
                var label = document.createElementNS(xmlns, 'text');
                label.setAttribute('x', x + Math.round(barWidth / 2));
                label.setAttribute('y', Math.max(10, y - 4));
                label.setAttribute('fill', '#495057');
                label.setAttribute('font-size', '10');
                label.setAttribute('text-anchor', 'middle');
                label.textContent = d;
                g.appendChild(label);
            });

            // month labels
            var monthNames = (function (count) {
                var names = [];
                if (typeof startYear === 'number') {
                    for (var y = 0; y < count; y++) {
                        names.push((startYear + y).toString());
                    }
                    return names;
                }
                var now = new Date();
                for (var j = count - 1; j >= 0; j--) {
                    var d = new Date(now.getFullYear(), now.getMonth() - j, 1);
                    names.push(d.toLocaleString(undefined, { month: 'short' }));
                }
                return names;
            })(series.length);
            monthNames.forEach(function (m, i) {
                var x = Math.round(i * band + band / 2);
                var text = document.createElementNS(xmlns, 'text');
                text.setAttribute('x', x);
                text.setAttribute('y', innerHeight + 16);
                text.setAttribute('fill', '#6c757d');
                text.setAttribute('font-size', '10');
                text.setAttribute('text-anchor', 'middle');
                text.textContent = m;
                g.appendChild(text);
            });

            // avg line (black)
            var avg = Math.round(series.reduce(function (a, b) { return a + b; }, 0) / series.length);
            var avgY = innerHeight - Math.round((avg / max) * innerHeight);
            var avgLine = document.createElementNS(xmlns, 'line');
            avgLine.setAttribute('x1', 0);
            avgLine.setAttribute('x2', innerWidth);
            avgLine.setAttribute('y1', avgY);
            avgLine.setAttribute('y2', avgY);
            avgLine.setAttribute('stroke', '#000');
            avgLine.setAttribute('stroke-width', '1');
            avgLine.setAttribute('stroke-dasharray', '4 4');
            g.appendChild(avgLine);
            var avgLabel = document.createElementNS(xmlns, 'text');
            avgLabel.setAttribute('x', innerWidth - 6);
            avgLabel.setAttribute('y', avgY - 6);
            avgLabel.setAttribute('fill', '#000');
            avgLabel.setAttribute('font-size', '11');
            avgLabel.setAttribute('text-anchor', 'end');
            avgLabel.textContent = 'Avg ' + avg;
            g.appendChild(avgLabel);

            // moving average (simple, window=3)
            var ma = calcMA(series, 3);
            var points = ma.map(function (v, i) {
                var x = Math.round(i * band + band / 2);
                var y = innerHeight - Math.round((v / max) * innerHeight);
                return x + ',' + y;
            }).join(' ');
            var maPolyline = document.createElementNS(xmlns, 'polyline');
            maPolyline.setAttribute('points', points);
            maPolyline.setAttribute('fill', 'none');
            maPolyline.setAttribute('stroke', '#000');
            maPolyline.setAttribute('stroke-width', '2');
            maPolyline.setAttribute('stroke-linejoin', 'round');
            maPolyline.setAttribute('stroke-linecap', 'round');
            g.appendChild(maPolyline);

            // tooltip support
            if (el._enrollmentTooltip && el._enrollmentTooltip.parentNode) {
                el._enrollmentTooltip.parentNode.removeChild(el._enrollmentTooltip);
                el._enrollmentTooltip = null;
            }
            var tooltip = createTooltip();
            el._enrollmentTooltip = tooltip;

            var rects = g.querySelectorAll('rect');
            rects.forEach(function (r, i) {
                r.addEventListener('mouseover', function (ev) {
                    var value = series[i];
                    var month = monthNames[i];
                    tooltip.innerHTML = '<strong>' + month + '</strong>: ' + value;
                    tooltip.style.display = 'block';
                    tooltip.style.left = (ev.pageX + 8) + 'px';
                    tooltip.style.top = (ev.pageY - 28) + 'px';
                    r.setAttribute('opacity', '0.85');
                });
                r.addEventListener('mousemove', function (ev) {
                    tooltip.style.left = (ev.pageX + 8) + 'px';
                    tooltip.style.top = (ev.pageY - 28) + 'px';
                });
                r.addEventListener('mouseout', function () {
                    tooltip.style.display = 'none';
                    r.setAttribute('opacity', '1');
                });
            });

            el.appendChild(svg);
        }

        function renderD3(el, series, startYear) {
            if (!el || !series || !series.length || typeof d3 === 'undefined') return;
            while (el.firstChild) el.removeChild(el.firstChild);
            var margin = { top: 20, right: 10, bottom: 40, left: 40 };
            var width = el.clientWidth || 400;
            var height = parseInt(el.style.height, 10) || 300;
            var innerWidth = Math.max(0, width - margin.left - margin.right);
            var innerHeight = Math.max(0, height - margin.top - margin.bottom);
            var data = series.map(function (d, i) { return { idx: i, label: i.toString(), value: +d }; });
            var max = d3.max(data, function (d) { return d.value; }) || 1;

            var monthNames = (function (count) {
                var names = [];
                if (typeof startYear === 'number') {
                    for (var y = 0; y < count; y++) names.push((startYear + y).toString());
                    return names;
                }
                var now = new Date();
                for (var i = count - 1; i >= 0; i--) {
                    var d = new Date(now.getFullYear(), now.getMonth() - i, 1);
                    names.push(d.toLocaleString(undefined, { month: 'short' }));
                }
                return names;
            })(data.length);

            var svg = d3.select(el).append('svg').attr('width', width).attr('height', height);
            var g = svg.append('g').attr('transform', 'translate(' + margin.left + ',' + margin.top + ')');

            var x = d3.scale.ordinal().domain(data.map(function (d) { return d.label; })).rangeRoundBands([0, innerWidth], 0.12);
            var y = d3.scale.linear().domain([0, max]).range([innerHeight, 0]);

            var palette = ['#4dabf7', '#a78bfa', '#f97316', '#34d399', '#f472b6', '#60a5fa', '#fb7185'];

            var yTicks = y.ticks ? y.ticks(4) : [0, max / 2, max];
            g.selectAll('line.grid').data(yTicks).enter().append('line').attr('class', 'grid').attr('x1', 0).attr('x2', innerWidth).attr('y1', function (d) { return y(d); }).attr('y2', function (d) { return y(d); }).attr('stroke', '#e9ecef');

            var barGroups = g.selectAll('.bar-group').data(data).enter().append('g').attr('class', 'bar-group');

            barGroups.append('rect').attr('class', 'bar').attr('x', function (d) { return x(d.label); }).attr('y', function (d) { return y(d.value); }).attr('width', x.rangeBand()).attr('height', function (d) { return innerHeight - y(d.value); }).attr('fill', function (d, i) { return palette[i % palette.length]; }).attr('rx', 3);

            barGroups.append('text').attr('x', function (d) { return x(d.label) + x.rangeBand() / 2; }).attr('y', function (d) { return Math.max(10, y(d.value) - 6); }).attr('text-anchor', 'middle').attr('fill', '#495057').attr('font-size', '10').text(function (d) { return d.value; });

            var xAxis = d3.svg.axis().scale(x).orient('bottom');
            var xAxisG = g.append('g').attr('transform', 'translate(0,' + innerHeight + ')').call(xAxis);
            xAxisG.selectAll('text').text(function (d, i) { return monthNames[i] || d; }).attr('dy', '1.2em').attr('fill', '#6c757d');

            var yAxis = d3.svg.axis().scale(y).orient('left').ticks(4);
            g.append('g').attr('class', 'y-axis').call(yAxis).selectAll('text').attr('fill', '#6c757d');

            var avg = Math.round(d3.sum(series) / series.length);
            var avgY = y(avg);
            g.append('line').attr('x1', 0).attr('x2', innerWidth).attr('y1', avgY).attr('y2', avgY).style('stroke', '#000').style('stroke-dasharray', '4 4');
            g.append('text').attr('x', innerWidth - 6).attr('y', avgY - 6).attr('text-anchor', 'end').attr('fill', '#000').attr('font-size', '11').text('Avg ' + avg);

            // moving average (window=3) - D3 renderer
            var maData = data.map(function (d, i) {
                var start = Math.max(0, i - 2);
                var s = 0, cnt = 0;
                for (var k = start; k <= i; k++) { s += series[k]; cnt++; }
                return { idx: i, label: i.toString(), value: s / cnt };
            });

            var lineGen = d3.svg.line()
                .x(function (d) { return x(d.label) + x.rangeBand() / 2; })
                .y(function (d) { return y(d.value); })
                .interpolate('linear');

            g.append('path').datum(maData).attr('class', 'ma-line').attr('d', lineGen).style('fill', 'none').style('stroke', '#000').style('stroke-width', 2);

            // tooltip (DOM element)
            if (el._enrollmentTooltip && el._enrollmentTooltip.parentNode) { el._enrollmentTooltip.parentNode.removeChild(el._enrollmentTooltip); el._enrollmentTooltip = null; }
            var tooltip = createTooltip();
            el._enrollmentTooltip = tooltip;

            g.selectAll('.bar')
                .on('mouseover', function (d, i) {
                    var ev = d3.event || window.event;
                    var month = monthNames[i] || ('M' + (i + 1));
                    tooltip.innerHTML = '<strong>' + month + '</strong>: ' + d.value;
                    tooltip.style.display = 'block';
                    tooltip.style.left = (ev.pageX + 8) + 'px';
                    tooltip.style.top = (ev.pageY - 28) + 'px';
                    d3.select(this).attr('opacity', 0.85);
                })
                .on('mousemove', function () { var ev = d3.event || window.event; tooltip.style.left = (ev.pageX + 8) + 'px'; tooltip.style.top = (ev.pageY - 28) + 'px'; })
                .on('mouseout', function () { tooltip.style.display = 'none'; d3.select(this).attr('opacity', 1); });
        }

        // expose init function
        if (window.sfBlazorSB) {
            window.sfBlazorSB.initEnrollmentChart = function (elementId, series, startYear) {
                try {
                    var el = document.getElementById(elementId);
                    if (!el) return;
                    if (!el._enrollmentChart) el._enrollmentChart = {};
                    // choose renderer
                    if (typeof d3 !== 'undefined' && d3.scale && d3.scale.ordinal) {
                        renderD3(el, series || [], startYear);
                    } else {
                        renderSvg(el, series || [], startYear);
                    }
                    // cleanup previous resize handler
                    if (el._enrollmentChart.resizeHandler) {
                        window.removeEventListener('resize', el._enrollmentChart.resizeHandler);
                    }
                    var resizeHandler = function () {
                        if (typeof d3 !== 'undefined' && d3.scale && d3.scale.ordinal) {
                            renderD3(el, series || [], startYear);
                        } else {
                            renderSvg(el, series || [], startYear);
                        }
                    };
                    el._enrollmentChart.resizeHandler = resizeHandler;
                    window.addEventListener('resize', resizeHandler);
                } catch (err) {
                    console.error('initEnrollmentChart error', err);
                }
            };
        }
    })();
}

export function initC3BarsPies(id, data) {
    if (typeof С3BarsPies !== 'undefined') {
        С3BarsPies.init(id, data);
    }
}
